/**
 * Dream Gift marketplace UI: add to cart, wishlist, carousels, hero slider,
 * category mega-menu, mobile drawer, predictive search, sticky header.
 * Loaded once from layout/theme.liquid. Every feature binds by data attribute,
 * so sections can be added, removed or re-rendered in the theme editor freely.
 */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

const root = () => window.Shopify?.routes?.root || '/';
const $$ = (sel, scope = document) => Array.from(scope.querySelectorAll(sel));
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const canHover = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
const t = (key, fallback) => window.DG?.strings?.[key] || fallback;

export function formatMoney(cents) {
  const value = (Number(cents) / 100).toFixed(2).replace('.', ',');
  return `${value}\u00a0€`;
}

/* ------------------------------------------------------------------ */
/* Toast                                                               */
/* ------------------------------------------------------------------ */

let toastTimer;
function toast(message, type = 'info') {
  let el = document.getElementById('dg-toast');
  if (!el) {
    el = document.createElement('div');
    el.id = 'dg-toast';
    el.className = 'dg-toast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    document.body.appendChild(el);
  }
  el.textContent = message;
  el.dataset.type = type;
  el.classList.add('is-visible');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove('is-visible'), 2600);
}

/* Replays a one-shot CSS animation class (cart nod, heart pop) */
function bump(elements, className = 'is-bumped') {
  if (reducedMotion()) return;
  for (const el of elements) {
    if (!el) continue;
    el.classList.remove(className);
    void el.offsetWidth;
    el.classList.add(className);
    el.addEventListener('animationend', () => el.classList.remove(className), { once: true });
  }
}

/* ------------------------------------------------------------------ */
/* Add to cart (fires Horizon's standard cart event, so the cart      */
/* drawer and the header cart bubble update themselves)               */
/* ------------------------------------------------------------------ */

async function fetchCart() {
  const res = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
  return res.json();
}

async function addToCart(button) {
  const variantId = button.dataset.variantId;
  if (!variantId || button.getAttribute('aria-disabled') === 'true') return;

  button.setAttribute('aria-disabled', 'true');
  button.classList.add('is-loading');

  const sectionIds = $$('cart-items-component')
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  const deferred = CartLinesUpdateEvent.createPromise();

  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const body = { items: [{ id: Number(variantId), quantity: 1 }] };
    if (sectionIds.length) body.sections = sectionIds.join(',');

    const res = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || data.status) {
      throw new Error(data.description || data.message || t('cartError', 'Продуктът не беше добавен.'));
    }

    const cart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'dg-product-card',
        sourceId: variantId,
        itemCount: 1,
        productId: button.dataset.productId,
        sections: data.sections,
        didError: false,
      },
    });

    button.classList.add('is-added');
    bump($$('.dg-action cart-icon').map((icon) => icon.closest('.dg-action__icon')));
    const label = button.querySelector('[data-dg-atc-label]');
    const original = label?.innerHTML;
    if (label) label.textContent = t('added', 'Добавено');
    if (!document.querySelector('cart-drawer-component[auto-open]')) toast(t('addedToast', 'Продуктът е добавен в количката'));
    setTimeout(() => {
      button.classList.remove('is-added');
      if (label && original) label.innerHTML = original;
    }, 2200);
  } catch (error) {
    try {
      const cart = await fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'dg-product-card', itemCount: 1 },
      });
    } catch (e) {
      deferred.reject(e);
    }
    button.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'INVALID' }));
    toast(error.message, 'error');
  } finally {
    button.removeAttribute('aria-disabled');
    button.classList.remove('is-loading');
  }
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-dg-atc]');
  if (!button) return;
  event.preventDefault();
  addToCart(button);
});

/* Header cart total follows every cart change (cards, drawer, product page) */
function updateCartTotals(cart) {
  for (const el of $$('[data-dg-cart-total]')) el.textContent = formatMoney(cart.total_price);
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(() => fetchCart())
    .then(updateCartTotals)
    .catch(() => {});
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) fetchCart().then(updateCartTotals).catch(() => {});
});

/* ------------------------------------------------------------------ */
/* Wishlist (browser storage, no account needed)                      */
/* ------------------------------------------------------------------ */

const WISH_KEY = 'dg:wishlist';

function readWishlist() {
  try {
    const list = JSON.parse(localStorage.getItem(WISH_KEY) || '[]');
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function writeWishlist(list) {
  try {
    localStorage.setItem(WISH_KEY, JSON.stringify(list.slice(0, 100)));
  } catch {
    /* storage full or blocked: ignore */
  }
  syncWishlistUI();
}

function syncWishlistUI() {
  const list = readWishlist();
  for (const button of $$('[data-dg-wish]')) {
    const on = list.includes(button.dataset.dgWish);
    button.setAttribute('aria-pressed', String(on));
    button.setAttribute('aria-label', on ? t('wishRemove', 'Премахни от любими') : t('wishAdd', 'Добави в любими'));
  }
  for (const counter of $$('[data-dg-wish-count]')) {
    counter.textContent = list.length ? String(list.length) : '';
    counter.hidden = list.length === 0;
  }
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-dg-wish]');
  if (!button) return;
  event.preventDefault();
  const handle = button.dataset.dgWish;
  const list = readWishlist();
  const index = list.indexOf(handle);
  if (index === -1) {
    list.unshift(handle);
    bump([button], 'is-popping');
    toast(t('wishAdded', 'Добавено в любими'));
  } else {
    list.splice(index, 1);
    toast(t('wishRemoved', 'Премахнато от любими'));
  }
  writeWishlist(list);
  const drawer = document.querySelector('[data-dg-wish-drawer]');
  if (drawer?.open) renderWishlist(drawer);
});

/* Button 046 markup for buttons built in JS (same as snippets/dg-button.liquid, small size) */
function button046(label, variant, tag, attrs) {
  return `<${tag} data-button-046 class="button-046 dg-btn dg-btn--${variant} dg-btn--sm" ${attrs}><span class="button-046__bg"></span><span class="button-046__bg-circle"><span data-button-046-circle class="button-046__circle-wrap"><span class="button-046__circle"></span></span></span><span class="button-046__inner"><span class="button-046__text">${label}</span></span></${tag}>`;
}

async function renderWishlist(drawer) {
  const body = drawer.querySelector('[data-dg-wish-body]');
  if (!body) return;
  const list = readWishlist();
  if (!list.length) {
    body.innerHTML = `<p class="dg-wish__empty">${t('wishEmpty', 'Все още нямате любими продукти. Натиснете сърцето върху продукт, за да го запазите.')}</p>`;
    return;
  }
  body.innerHTML = `<p class="dg-wish__loading">${t('loading', 'Зареждане…')}</p>`;
  const products = await Promise.all(
    list.map((handle) =>
      fetch(`${root()}products/${encodeURIComponent(handle)}.js`)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null)
    )
  );
  const valid = products.filter(Boolean);
  if (valid.length !== list.length) writeWishlist(valid.map((p) => p.handle));
  body.innerHTML = `<ul class="dg-wish__list">${valid
    .map((p) => {
      const img = p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=160` : '';
      const variant = p.variants?.[0];
      const atc =
        p.available && variant && p.variants.length === 1 && !(p.tags || []).includes('персонализиран')
          ? button046(`<span data-dg-atc-label>${t('addToCart', 'Добави')}</span>`, 'primary', 'button', `type="button" data-dg-atc data-variant-id="${variant.id}" data-product-id="${p.id}"`)
          : button046(t('view', 'Виж'), 'outline', 'a', `href="${p.url}"`);
      return `<li class="dg-wish__item">
        <a class="dg-wish__media" href="${p.url}">${img ? `<img src="${img}" alt="" width="80" height="80" loading="lazy">` : ''}</a>
        <div class="dg-wish__info"><a class="dg-wish__title" href="${p.url}">${p.title}</a><span class="dg-wish__price">${formatMoney(p.price)}</span></div>
        <div class="dg-wish__actions">${atc}<button type="button" class="dg-wish__remove" data-dg-wish="${p.handle}" aria-label="${t('wishRemove', 'Премахни от любими')}">×</button></div>
      </li>`;
    })
    .join('')}</ul>`;
}

/* ------------------------------------------------------------------ */
/* Dialog drawers (mobile menu, wishlist)                             */
/* ------------------------------------------------------------------ */

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-dg-open]');
  if (opener) {
    const dialog = document.getElementById(opener.dataset.dgOpen);
    if (dialog instanceof HTMLDialogElement) {
      event.preventDefault();
      dialog.showModal();
      document.documentElement.setAttribute('scroll-lock', '');
      if (dialog.matches('[data-dg-wish-drawer]')) renderWishlist(dialog);
    }
    return;
  }
  const closer = event.target.closest('[data-dg-close]');
  if (closer) {
    closer.closest('dialog')?.close();
    return;
  }
  // Click on the backdrop closes a drawer.
  if (event.target instanceof HTMLDialogElement && event.target.matches('.dg-drawer')) {
    event.target.close();
  }
});

document.addEventListener(
  'close',
  (event) => {
    if (event.target instanceof HTMLDialogElement && event.target.matches('.dg-drawer')) {
      document.documentElement.removeAttribute('scroll-lock');
    }
  },
  true
);

/* ------------------------------------------------------------------ */
/* Product carousels                                                  */
/* ------------------------------------------------------------------ */

function initCarousel(carousel) {
  if (carousel.dataset.dgReady) return;
  carousel.dataset.dgReady = '1';
  const track = carousel.querySelector('[data-dg-track]');
  const prev = carousel.querySelector('[data-dg-prev]');
  const next = carousel.querySelector('[data-dg-next]');
  if (!track) return;

  const update = () => {
    const max = track.scrollWidth - track.clientWidth - 2;
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= max;
    carousel.classList.toggle('is-scrollable', max > 2);
  };
  const step = (dir) => {
    const item = track.querySelector('[data-dg-item]');
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const itemWidth = item ? item.getBoundingClientRect().width + gap : track.clientWidth;
    const perPage = Math.max(1, Math.floor((track.clientWidth + gap) / itemWidth));
    track.scrollBy({ left: dir * perPage * itemWidth, behavior: reducedMotion() ? 'auto' : 'smooth' });
  };

  prev?.addEventListener('click', () => step(-1));
  next?.addEventListener('click', () => step(1));
  let raf;
  track.addEventListener('scroll', () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(update);
  });
  new ResizeObserver(update).observe(track);
  update();
}

/* ------------------------------------------------------------------ */
/* Hero slider (scroll-snap track: swipe works natively on touch)     */
/* ------------------------------------------------------------------ */

function initSlider(slider) {
  if (slider.dataset.dgReady) return;
  slider.dataset.dgReady = '1';
  const track = slider.querySelector('[data-dg-slides]');
  const slides = $$('[data-dg-slide]', slider);
  const dots = $$('[data-dg-dot]', slider);
  if (!track || slides.length < 2) return;

  let index = 0;
  let timer;
  const interval = Number(slider.dataset.interval || 6) * 1000;

  const goTo = (i, smooth = true) => {
    index = (i + slides.length) % slides.length;
    track.scrollTo({ left: slides[index].offsetLeft - track.offsetLeft, behavior: smooth && !reducedMotion() ? 'smooth' : 'auto' });
  };
  const setActive = (i) => {
    index = i;
    dots.forEach((dot, n) => dot.setAttribute('aria-current', String(n === i)));
    slides.forEach((slide, n) => {
      slide.toggleAttribute('inert', n !== i);
      slide.classList.toggle('is-active', n === i);
    });
  };
  const play = () => {
    if (reducedMotion() || slider.dataset.autoplay !== 'true') return;
    stop();
    slider.classList.remove('is-paused');
    timer = setInterval(() => goTo(index + 1), interval);
  };
  const stop = () => {
    clearInterval(timer);
    slider.classList.add('is-paused');
  };

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio > 0.6) setActive(slides.indexOf(entry.target));
      }
    },
    { root: track, threshold: [0.6] }
  );
  slides.forEach((slide) => io.observe(slide));

  slider.querySelector('[data-dg-slide-prev]')?.addEventListener('click', () => goTo(index - 1));
  slider.querySelector('[data-dg-slide-next]')?.addEventListener('click', () => goTo(index + 1));
  dots.forEach((dot, n) => dot.addEventListener('click', () => goTo(n)));

  slider.addEventListener('pointerenter', stop);
  slider.addEventListener('pointerleave', play);
  slider.addEventListener('focusin', stop);
  slider.addEventListener('focusout', play);
  track.addEventListener('touchstart', stop, { passive: true });
  document.addEventListener('visibilitychange', () => (document.hidden ? stop() : play()));

  setActive(0);
  play();
}

/* ------------------------------------------------------------------ */
/* Category mega-menu (vertical list with flyout panels)             */
/* ------------------------------------------------------------------ */

function initCatMenu(menu) {
  if (menu.dataset.dgReady) return;
  menu.dataset.dgReady = '1';
  const items = $$('[data-dg-cat-item]', menu);
  let openTimer;
  let closeTimer;

  const activate = (item) => {
    for (const other of items) {
      const on = other === item;
      other.classList.toggle('is-active', on);
      other.querySelector('[data-dg-cat-link]')?.setAttribute('aria-expanded', String(on && other.hasAttribute('data-has-panel')));
    }
    menu.classList.toggle('has-active', Boolean(item && item.hasAttribute('data-has-panel')));
  };

  for (const item of items) {
    item.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(closeTimer);
      clearTimeout(openTimer);
      // Switching between items waits a beat so a diagonal move towards the open panel
      // does not flip it to a neighbouring category.
      const delay = menu.classList.contains('has-active') ? 110 : 40;
      openTimer = setTimeout(() => activate(item), delay);
    });
    item.addEventListener('focusin', () => activate(item));
  }

  menu.addEventListener('pointerleave', (event) => {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(openTimer);
    closeTimer = setTimeout(() => activate(null), 180);
  });
  menu.addEventListener('pointerenter', () => clearTimeout(closeTimer));
  menu.addEventListener('focusout', (event) => {
    if (!menu.contains(event.relatedTarget)) activate(null);
  });
  menu.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') activate(null); // the dropdown wrapper closes itself and restores focus
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      const links = items.map((i) => i.querySelector('[data-dg-cat-link]'));
      const current = links.indexOf(document.activeElement);
      if (current === -1) return;
      event.preventDefault();
      const nextIndex = (current + (event.key === 'ArrowDown' ? 1 : -1) + links.length) % links.length;
      links[nextIndex]?.focus();
    }
  });
}

/* Header "Категории" dropdown, with the page dimmed below the category bar */
function dimmer() {
  let el = document.querySelector('.dg-dim');
  if (!el) {
    el = document.createElement('div');
    el.className = 'dg-dim';
    el.setAttribute('aria-hidden', 'true');
    document.body.appendChild(el);
  }
  return el;
}
function openDropdown(dropdown) {
  if (dropdown.hasAttribute('data-dg-home-list') && !dropdown.classList.contains('is-live')) return;
  if (!dropdown.classList.contains('is-open')) dropdown.dataset.openedAt = String(Date.now());
  dropdown.classList.add('is-open');
  dropdown.querySelector('[data-dg-cat-toggle]')?.setAttribute('aria-expanded', 'true');
  const bar = dropdown.closest('.dg-nav') || dropdown;
  const dim = dimmer();
  dim.style.setProperty('--dg-dim-top', `${Math.max(0, bar.getBoundingClientRect().bottom)}px`);
  dim.classList.add('is-visible');
}
function closeDropdown(dropdown) {
  document.querySelector('.dg-dim')?.classList.remove('is-visible');
  dropdown.classList.remove('is-open');
  dropdown.querySelector('[data-dg-cat-toggle]')?.setAttribute('aria-expanded', 'false');
  dropdown.querySelectorAll('[data-dg-cat-item].is-active').forEach((i) => i.classList.remove('is-active'));
  dropdown.querySelector('[data-dg-catmenu]')?.classList.remove('has-active');
}

function initCatDropdown(dropdown) {
  if (dropdown.dataset.dgReady) return;
  dropdown.dataset.dgReady = '1';
  const toggle = dropdown.querySelector('[data-dg-cat-toggle]');
  let timer;
  toggle?.addEventListener('click', (event) => {
    event.preventDefault();
    clearTimeout(timer);
    // Hover usually opens it a moment before the click lands; that click shouldn't shut it again.
    const justOpened = Date.now() - Number(dropdown.dataset.openedAt || 0) < 700;
    if (dropdown.classList.contains('is-open') && !justOpened) closeDropdown(dropdown);
    else openDropdown(dropdown);
  });
  dropdown.addEventListener('pointerenter', (event) => {
    if (event.pointerType !== 'mouse' || !canHover()) return;
    clearTimeout(timer);
    timer = setTimeout(() => openDropdown(dropdown), 120);
  });
  dropdown.addEventListener('pointerleave', (event) => {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(timer);
    timer = setTimeout(() => closeDropdown(dropdown), 220);
  });
  document.addEventListener('click', (event) => {
    if (!dropdown.contains(event.target)) closeDropdown(dropdown);
  });
  // Escape closes it wherever focus is (toggle or panel) and returns focus to the toggle.
  dropdown.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !dropdown.classList.contains('is-open')) return;
    clearTimeout(timer);
    closeDropdown(dropdown);
    toggle?.focus();
  });
}

/* ------------------------------------------------------------------ */
/* Predictive search                                                   */
/* ------------------------------------------------------------------ */

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

function initSearch(form) {
  if (form.dataset.dgReady) return;
  form.dataset.dgReady = '1';
  const input = form.querySelector('[data-dg-search-input]');
  const panel = form.querySelector('[data-dg-search-results]');
  if (!input || !panel) return;
  let controller;
  let timer;
  let activeIndex = -1;

  const emptyState = form.querySelector('[data-dg-search-empty]');
  const header = form.closest('[data-dg-header]');

  const close = () => {
    panel.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    activeIndex = -1;
    if (form.classList.contains('is-searching')) {
      form.classList.remove('is-searching');
      document.querySelector('.dg-dim')?.classList.remove('is-visible', 'is-search');
    }
  };

  // Clicking into the field opens the search panel straight away: popular categories and
  // bestsellers until there's a query, then live suggestions. The page dims behind it.
  const open = () => {
    if (!form.classList.contains('is-searching')) {
      form.classList.add('is-searching');
      const dim = dimmer();
      dim.style.setProperty('--dg-dim-top', `${Math.max(0, (header || form).getBoundingClientRect().bottom)}px`);
      dim.classList.add('is-visible', 'is-search');
    }
    panel.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  };

  const showEmptyState = () => {
    if (!emptyState) return;
    panel.innerHTML = emptyState.innerHTML;
    activeIndex = -1;
    open();
  };

  input.addEventListener('focus', () => {
    if (input.value.trim().length < 2) showEmptyState();
    else if (panel.innerHTML) open();
  });
  input.addEventListener('click', () => {
    if (panel.hidden && input.value.trim().length < 2) showEmptyState();
  });

  const render = (data, query) => {
    const products = data?.resources?.results?.products || [];
    const collections = data?.resources?.results?.collections || [];
    if (!products.length && !collections.length) {
      panel.innerHTML = `<p class="dg-suggest__empty">${t('noResults', 'Няма резултати за')} „${escapeHtml(query)}“</p>`;
    } else {
      const cols = collections
        .slice(0, 4)
        .map((c) => `<li><a class="dg-suggest__chip" role="option" href="${c.url}">${escapeHtml(c.title)}</a></li>`)
        .join('');
      const prods = products
        .slice(0, 6)
        .map((p) => {
          const img = p.image ? `${p.image}${p.image.includes('?') ? '&' : '?'}width=120` : '';
          return `<li><a class="dg-suggest__product" role="option" href="${p.url}">
            <span class="dg-suggest__img">${img ? `<img src="${img}" alt="" width="48" height="48" loading="lazy">` : ''}</span>
            <span class="dg-suggest__title">${escapeHtml(p.title)}</span>
            <span class="dg-suggest__price">${formatMoney(Math.round(parseFloat(p.price) * 100))}</span></a></li>`;
        })
        .join('');
      panel.innerHTML = `${cols ? `<p class="dg-suggest__label">${t('categories', 'Категории')}</p><ul class="dg-suggest__chips" role="presentation">${cols}</ul>` : ''}
        ${prods ? `<p class="dg-suggest__label">${t('products', 'Продукти')}</p><ul class="dg-suggest__products" role="presentation">${prods}</ul>` : ''}
        <a class="dg-suggest__all" href="${root()}search?q=${encodeURIComponent(query)}&type=product">${t('allResults', 'Всички резултати за')} „${escapeHtml(query)}“ →</a>`;
    }
    open();
    activeIndex = -1;
  };

  input.addEventListener('input', () => {
    const query = input.value.trim();
    clearTimeout(timer);
    if (query.length < 2) {
      controller?.abort();
      showEmptyState();
      return;
    }
    timer = setTimeout(async () => {
      controller?.abort();
      controller = new AbortController();
      try {
        const url = `${root()}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection&resources[limit]=6&resources[options][unavailable_products]=last`;
        const res = await fetch(url, { signal: controller.signal });
        render(await res.json(), query);
      } catch (error) {
        if (error.name !== 'AbortError') close();
      }
    }, 180);
  });

  input.addEventListener('keydown', (event) => {
    const options = $$('[role="option"]', panel);
    if (event.key === 'Escape') return close();
    if (!options.length || panel.hidden) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      activeIndex = (activeIndex + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
      options.forEach((o, i) => o.classList.toggle('is-active', i === activeIndex));
      options[activeIndex].scrollIntoView({ block: 'nearest' });
    }
    if (event.key === 'Enter' && activeIndex > -1) {
      event.preventDefault();
      options[activeIndex].click();
    }
  });

  document.addEventListener('click', (event) => {
    if (!form.contains(event.target)) close();
  });
}

/* ------------------------------------------------------------------ */
/* Sticky header shadow                                               */
/* ------------------------------------------------------------------ */

function initStickyHeader(header) {
  if (header.dataset.dgReady) return;
  header.dataset.dgReady = '1';
  const nav = document.querySelector('.dg-nav');
  const desktop = window.matchMedia('(min-width: 990px)');
  // Horizon's desktop "squeeze" layout scrolls .page-wrapper instead of the window,
  // so the sentinel lives at the top of the scrolling content.
  const sentinel = document.createElement('div');
  sentinel.setAttribute('aria-hidden', 'true');
  sentinel.style.cssText = 'height:1px;margin-bottom:-1px;pointer-events:none;';
  (document.querySelector('.page-wrapper') || document.body).prepend(sentinel);
  new IntersectionObserver(
    ([entry]) => {
      header.classList.toggle('is-stuck', !entry.isIntersecting);
      nav?.classList.toggle('is-stuck', !entry.isIntersecting);
    },
    { rootMargin: '120px 0px 0px 0px' }
  ).observe(sentinel);

  // Horizon templates (product page sticky details, sticky sidebars) read these variables;
  // on desktop the category bar sticks under the header, so it counts towards the offset.
  const publish = () => {
    const headerH = header.offsetHeight;
    const navH = desktop.matches && nav ? nav.offsetHeight : 0;
    let groupH = 0;
    for (const el of document.querySelectorAll('#header-group > .shopify-section')) {
      if (!el.contains(header)) groupH += el.offsetHeight;
    }
    document.body.style.setProperty('--dg-header-h', `${headerH}px`);
    document.body.style.setProperty('--header-height', `${headerH}px`);
    document.body.style.setProperty('--header-group-height', `${groupH}px`);
    document.body.style.setProperty('--sticky-header-offset', `${headerH + navH}px`);
  };
  publish();
  const resize = new ResizeObserver(publish);
  resize.observe(header);
  if (nav) resize.observe(nav);
  desktop.addEventListener('change', publish);

  // Homepage: "Категории" opens as a dropdown only once the hero's own list is out of view
  const homeDropdown = document.querySelector('[data-dg-home-list]');
  const heroList = document.querySelector('.dg-hero__cats');
  if (homeDropdown && heroList) {
    new IntersectionObserver(([entry]) => {
      const live = !entry.isIntersecting;
      homeDropdown.classList.toggle('is-live', live);
      if (!live) closeDropdown(homeDropdown);
    }).observe(heroList);
  } else {
    homeDropdown?.classList.add('is-live');
  }
}

/* ------------------------------------------------------------------ */
/* Button 046 by Osmo [https://osmo.supply/]: the hover fill grows     */
/* from the cursor. GSAP and CustomEase load before this module.       */
/* ------------------------------------------------------------------ */

if (window.gsap && window.CustomEase) {
  const { gsap, CustomEase } = window;

  gsap.registerPlugin(CustomEase);
  CustomEase.create('button-046-ease', '0.32, 0.72, 0, 1');

  // Buttons rendered later (cart drawer, sticky bar, wishlist) are bound once each
  const bound046 = new WeakSet();

  function initButton046() {
    const buttons = [...document.querySelectorAll('[data-button-046]')].filter((button) => !bound046.has(button));
    if (buttons.length === 0) return;

    let mm = gsap.matchMedia();

    buttons.forEach((button) => {
      const circle = button.querySelector('[data-button-046-circle]');
      if (!circle) return;
      bound046.add(button);

      mm.add('(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)', () => {
        const xSet = gsap.quickSetter(circle, 'xPercent');
        const ySet = gsap.quickSetter(circle, 'yPercent');

        function getXY(e) {
          const { left, top, width, height } = button.getBoundingClientRect();
          const xTransform = gsap.utils.pipe(gsap.utils.mapRange(0, width, 0, 100), gsap.utils.clamp(0, 100));
          const yTransform = gsap.utils.pipe(gsap.utils.mapRange(0, height, 0, 100), gsap.utils.clamp(0, 100));

          return {
            x: xTransform(e.clientX - left),
            y: yTransform(e.clientY - top),
          };
        }

        function onEnter(e) {
          const { x, y } = getXY(e);
          xSet(x);
          ySet(y);
          gsap.to(circle, {
            scale: 1,
            duration: 1.25,
            ease: 'button-046-ease',
            overwrite: 'auto',
          });
        }

        function onLeave(e) {
          const { x, y } = getXY(e);

          gsap.killTweensOf(circle);

          gsap.to(circle, {
            xPercent: x > 90 ? x + 25 : x < 12.5 ? x - 25 : x,
            yPercent: y > 90 ? y + 25 : y < 12.5 ? y - 25 : y,
            scale: 0,
            duration: 0.45,
            ease: 'button-046-ease',
            overwrite: 'auto',
          });
        }

        function onMove(e) {
          const { x, y } = getXY(e);

          gsap.to(circle, {
            xPercent: x,
            yPercent: y,
            duration: 0.5,
            ease: 'power1',
            overwrite: 'auto',
          });
        }

        button.addEventListener('pointerenter', onEnter);
        button.addEventListener('pointerleave', onLeave);
        button.addEventListener('pointermove', onMove);

        return () => {
          button.removeEventListener('pointerenter', onEnter);
          button.removeEventListener('pointerleave', onLeave);
          button.removeEventListener('pointermove', onMove);
        };
      });
    });
  }

  // Initialize Button 046
  document.addEventListener('DOMContentLoaded', () => {
    document.fonts.ready.then(function () {
      initButton046();
      let queued = false;
      new MutationObserver(() => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          initButton046();
        });
      }).observe(document.body, { childList: true, subtree: true });
    });
  });
}

/* ------------------------------------------------------------------ */
/* Sections below the fold rise in once as they scroll into view       */
/* ------------------------------------------------------------------ */

let revealObserver;
function initReveal(scope = document) {
  if (reducedMotion() || window.Shopify?.designMode || !('IntersectionObserver' in window)) return;
  revealObserver ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        revealObserver.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px' }
  );
  for (const section of $$('.dg-section', scope)) {
    if (section.dataset.dgReveal || section.querySelector('.dg-hero')) continue;
    section.dataset.dgReveal = '1';
    // Only what starts below the fold, so nothing visible ever blinks
    if (section.getBoundingClientRect().top < window.innerHeight) continue;
    section.classList.add('dg-reveal');
    revealObserver.observe(section);
  }
}

/* ------------------------------------------------------------------ */
/* Boot + theme editor re-init                                        */
/* ------------------------------------------------------------------ */

function init(scope = document) {
  $$('[data-dg-carousel]', scope).forEach(initCarousel);
  $$('[data-dg-slider]', scope).forEach(initSlider);
  $$('[data-dg-catmenu]', scope).forEach(initCatMenu);
  $$('[data-dg-cat-dropdown]', scope).forEach(initCatDropdown);
  $$('[data-dg-search]', scope).forEach(initSearch);
  $$('[data-dg-header]', scope).forEach(initStickyHeader);
  initReveal(scope);
  syncWishlistUI();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

document.addEventListener('shopify:section:load', (event) => init(event.target));
window.addEventListener('storage', (event) => {
  if (event.key === WISH_KEY) syncWishlistUI();
});
